import { useState } from "react";
import { User, UpdateUserData } from "@/hooks/useUsersApi";
import { createApiService } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/atoms/dialog";
import { Button } from "@/components/atoms/button";
import { ArchiveX, ArchiveRestore } from "lucide-react";
import InvitationStatusMessage from "./InvitationStatusMessage";

interface UserDeactivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onDeactivationSuccess?: (userId: number) => void;
  mode?: "deactivate" | "reactivate";
}

export default function UserDeactivationModal({
  isOpen,
  onClose,
  user,
  onDeactivationSuccess,
  mode = "deactivate",
}: UserDeactivationModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusType, setStatusType] = useState<"success" | "error" | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { getAccessToken } = useAuth();

  // Función local para actualizar usuario
  const updateUser = async (id: number, userData: UpdateUserData) => {
    try {
      const token = await getAccessToken();
      const usersService = createApiService<User>("/auth/users", () =>
        Promise.resolve(token),
      );

      const updatedUser = await usersService.update(id, userData);
      toastHandler({
        description: `Usuario actualizado exitosamente`,
        title: "Éxito",
        variant: "success",
      });

      return updatedUser;
    } catch (error) {
      console.error("Error updating user:", error);
      toastHandler({
        description:
          error instanceof Error
            ? error.message
            : "Error al actualizar usuario",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  const handleDeactivate = async () => {
    if (!user) return;

    const isDeactivating = mode === "deactivate";
    setIsProcessing(true);
    try {
      await updateUser(user.id, { active: !isDeactivating });
      setStatusType("success");
      // Llamar al callback de éxito para actualizar inmediatamente la UI
      if (onDeactivationSuccess) {
        onDeactivationSuccess(user.id);
      }
      // No necesitamos refetchUsers porque el estado se actualiza localmente
    } catch (error) {
      console.error(
        `Error ${isDeactivating ? "deactivating" : "reactivating"} user:`,
        error,
      );
      const errorMsg =
        error instanceof Error
          ? error.message
          : `Error desconocido al ${isDeactivating ? "desactivar" : "reactivar"} el usuario`;
      setErrorMessage(errorMsg);
      setStatusType("error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setStatusType(null);
    setErrorMessage("");
    setIsProcessing(false);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(v: boolean) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent className="max-w-md w-full p-0 rounded-2xl overflow-hidden bg-white [&>button]:hidden">
        {/* Accessibility components - visually hidden but accessible to screen readers */}
        <DialogTitle className="sr-only">
          {mode === "deactivate"
            ? "Confirmar desactivación de usuario"
            : "Confirmar reactivación de usuario"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {mode === "deactivate"
            ? "Modal de confirmación para desactivar un usuario del sistema"
            : "Modal de confirmación para reactivar un usuario del sistema"}
        </DialogDescription>

        {!statusType ? (
          <div className="flex flex-col items-center justify-center p-6 relative">
            {/* Icon Circle */}
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E5F2FF]">
                <div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-full"
                  style={{
                    backgroundColor:
                      mode === "deactivate" ? "#DC2626" : "#10B978",
                  }}
                >
                  {mode === "deactivate" ? (
                    <ArchiveX className="w-8 h-8 text-white" />
                  ) : (
                    <ArchiveRestore className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>
            </div>

            {/* Title and Description */}
            <div className="text-xl font-semibold mb-2 text-center text-gray-900">
              {mode === "deactivate"
                ? "¿Estás seguro que quieres desactivar a este usuario?"
                : "¿Estás seguro que quieres reactivar a este usuario?"}
            </div>
            <div className="text-center text-gray-600 mb-6 px-4">
              {mode === "deactivate"
                ? "Si lo desactivas, sus tareas y pedidos quedarán suspendidos.\nPuedes volver a activarlo más tarde."
                : "Si lo reactivas, volverá a tener acceso al sistema y podrá realizar sus tareas normalmente."}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isProcessing}
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeactivate}
                disabled={isProcessing}
                className={`flex-1 text-white ${
                  mode === "deactivate"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isProcessing
                  ? mode === "deactivate"
                    ? "Desactivando..."
                    : "Reactivando..."
                  : mode === "deactivate"
                    ? "Sí, desactivar"
                    : "Sí, reactivar"}
              </Button>
            </div>
          </div>
        ) : (
          <InvitationStatusMessage
            type={statusType}
            email={user.email}
            errorMessage={errorMessage}
            onClose={handleClose}
            mode={mode === "deactivate" ? "deactivation" : "reactivation"}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
